<template>
  <div class="fu-drform" data-test="fu-drawer-email-form">
    <template v-if="templates.length">
      <div>
        <label class="fu-drform__label" :for="`${uid}-tpl`">Template</label>
        <select
          :id="`${uid}-tpl`"
          class="fu-drform__select"
          :value="templateId === null || templateId === undefined ? '' : String(templateId)"
          data-test="fu-drawer-email-template"
          @change="onTemplate($event.target.value)"
        >
          <option value="" disabled>Pick a template</option>
          <option v-for="t in templates" :key="t.value" :value="String(t.value)">{{ t.label }}</option>
        </select>
      </div>

      <div class="fu-drform__preview-card" data-test="fu-drawer-email-preview">
        <div class="fu-drform__preview-to">To <strong>{{ to || 'the patient' }}</strong></div>
        <div class="fu-drform__preview-body">
          <div class="fu-drform__preview-subject">{{ selectedLabel || 'No template picked yet' }}</div>
          <div v-if="note" class="fu-drform__preview-note">
            <span class="fu-drform__preview-k">Logged to the commlog</span>
            {{ note }}
          </div>
        </div>
      </div>
    </template>
    <div v-else class="fu-drform__warn" data-test="fu-drawer-email-empty">
      <fu-icon name="alert" :size="15" />
      <span>No email templates yet — create one in the Wiki first. <a href="/app/wiki">Open the Wiki</a></span>
    </div>

    <slot />
  </div>
</template>

<script>
/**
 * DrawerEmailForm — the Email method (today's form, CreateFollowup.vue:581–642): the template picker
 * and a preview of who it goes to, which template, and the note it logs; the design's empty state
 * when there is no email template. The default slot takes the next-date control.
 *
 *   templates   [{ value: WikiPageID, label: Name }] — the container lists them with today's email tag
 *               and wikiTags.withoutReviewRequestTemplates (template-variable-validation PICKERS)
 *   templateId  the picked template, or null
 *   to          the patient's email address
 *   note        the commlog note (drawerPayload.emailNoteForTemplate)
 * bug 8 (fixed): a pick only emits — nothing here focuses an element that may not exist.
 * Events: template-change(id). Presentational.
 */
import FuIcon from '../FuIcon.vue';

let seq = 0;

export default {
  name: 'DrawerEmailForm',
  components: { FuIcon },
  props: {
    templates: { type: Array, default: () => [] },
    templateId: { type: [Number, String], default: null },
    to: { type: String, default: '' },
    note: { type: String, default: '' },
  },
  data() {
    seq += 1;
    return { uid: `fu-dremail-${seq}` };
  },
  computed: {
    selectedLabel() {
      const t = this.templates.find(x => String(x.value) === String(this.templateId));
      return t ? t.label : '';
    },
  },
  methods: {
    onTemplate(v) {
      const t = this.templates.find(x => String(x.value) === v);
      this.$emit('template-change', t ? t.value : null);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './drawer';

.fu-drform { display: flex; flex-direction: column; gap: 16px; }
.fu-drform__label { @include fu-dr-field-label; }
.fu-drform__select { @include fu-input; }

.fu-drform__preview-card {
  border: 1px solid $fu-border;
  border-radius: 11px;
  overflow: hidden;
}

.fu-drform__preview-to {
  padding: 9px 13px;
  background: $fu-neutral-25;
  border-bottom: 1px solid $fu-border;
  font-size: 12.5px;
  color: $fu-text-muted;
  overflow-wrap: anywhere;
  strong { color: $fu-text; font-weight: $fu-fw-bold; }
}

.fu-drform__preview-body { padding: 12px 13px; }
.fu-drform__preview-subject { font-size: 14px; font-weight: $fu-fw-bold; }

.fu-drform__preview-note {
  margin-top: 6px;
  font-size: 13px;
  line-height: 1.55;
  color: $fu-neutral-600;
  white-space: pre-line;
  overflow-wrap: anywhere;
}

.fu-drform__preview-k {
  display: block;
  @include fu-eyebrow($fu-text-subtle);
  margin-bottom: 2px;
}

.fu-drform__warn {
  @include fu-dr-warn-strip;
  .fu-icon { flex: none; margin-top: 1px; color: $fu-warning; }
  a { color: $fu-link; font-weight: $fu-fw-semibold; }
}

.fu-dr--phone .fu-drform__select { @include fu-dr-phone-input; }
</style>
